/**
 * AdminIntegrationKeys — stub setup page for third-party API keys.
 *
 * BGV vendors: Springverify, IDfy, Checkr
 * Salary APIs: Levels.fyi, AmbitionBox
 *
 * MVP scope: form fields + save-to-DB (encrypted at rest via existing
 * env-secret pattern). No actual vendor SDK is wired yet — those live
 * in Phase 2 alongside webhook receivers and status sync.
 *
 * Keys are stored per-app under `integration_keys` collection, keyed by
 * (vendor, environment). Only admins can read/write them.
 */
import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Save, ShieldCheck, DollarSign, ExternalLink } from "lucide-react";

const VENDORS = {
    bgv: [
        { key: "springverify", name: "Springverify", docs: "https://springverify.com/developers", fields: ["api_key", "webhook_secret"] },
        { key: "idfy",         name: "IDfy",         docs: "https://developer.idfy.com/", fields: ["api_key", "account_id", "webhook_secret"] },
        { key: "checkr",       name: "Checkr",       docs: "https://docs.checkr.com/", fields: ["api_key", "webhook_secret"] },
    ],
    salary: [
        { key: "levels_fyi",   name: "Levels.fyi",   docs: "https://www.levels.fyi/", fields: ["api_key"] },
        { key: "ambitionbox",  name: "AmbitionBox",  docs: "https://www.ambitionbox.com/", fields: ["api_key"] },
    ],
};

export default function AdminIntegrationKeys() {
    const [keys, setKeys] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/integration-keys");
                setKeys(data?.keys || {});
            } catch (err) {
                // On 404 (endpoint not deployed yet) or any error, start with empty state
                if (err?.response?.status !== 404) toast.error(formatApiError(err));
            } finally { setLoading(false); }
        })();
    }, []);

    const save = async (vendorKey, patch) => {
        setSaving(vendorKey);
        try {
            const next = { ...keys, [vendorKey]: { ...(keys[vendorKey] || {}), ...patch } };
            await api.patch("/admin/integration-keys", { keys: next });
            setKeys(next);
            toast.success(`${vendorKey} keys saved`);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(null); }
    };

    if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    return (
        <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6" data-testid="admin-integration-keys-page">
            <div>
                <h1 className="font-display font-bold text-2xl">Integration keys</h1>
                <p className="text-sm text-muted-foreground mt-1">API credentials for third-party BGV vendors and salary intelligence providers. Keys are encrypted at rest.</p>
            </div>

            <VendorGroup
                title="Background Verification"
                icon={<ShieldCheck className="w-4 h-4" />}
                vendors={VENDORS.bgv}
                keys={keys}
                saving={saving}
                onSave={save}
                phase="Wiring is Phase 2 — save keys now so we can activate them when the vendor integration ships."
            />
            <VendorGroup
                title="Salary intelligence"
                icon={<DollarSign className="w-4 h-4" />}
                vendors={VENDORS.salary}
                keys={keys}
                saving={saving}
                onSave={save}
                phase="Currently Claude reasons about market ranges. These APIs will augment reasoning with real data once wired (Phase 2)."
            />
        </div>
    );
}

function VendorGroup({ title, icon, vendors, keys, saving, onSave, phase }) {
    return (
        <section className="border border-border bg-white" data-testid={`vendor-group-${title.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="px-4 py-3 border-b border-border bg-secondary/40 flex items-center gap-2">
                {icon}
                <h2 className="font-display font-bold text-base flex-1">{title}</h2>
                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Phase 2</span>
            </div>
            {phase && <div className="px-4 py-2 text-xs text-amber-800 bg-amber-50/50 border-b border-amber-200">{phase}</div>}
            <div className="divide-y divide-border">
                {vendors.map((v) => (
                    <VendorRow key={v.key} vendor={v} state={keys[v.key] || {}} saving={saving === v.key} onSave={(p) => onSave(v.key, p)} />
                ))}
            </div>
        </section>
    );
}

function VendorRow({ vendor, state, saving, onSave }) {
    const [form, setForm] = useState(() => Object.fromEntries(vendor.fields.map((f) => [f, state[f] || ""])));
    const [enabled, setEnabled] = useState(!!state.enabled);
    const hasKeys = vendor.fields.every((f) => (form[f] || "").trim());
    return (
        <div className="p-4 grid grid-cols-1 md:grid-cols-[240px_1fr_auto] gap-3 items-start" data-testid={`vendor-row-${vendor.key}`}>
            <div>
                <div className="flex items-center gap-2 text-sm font-semibold">
                    <KeyRound className="w-3.5 h-3.5 text-muted-foreground" /> {vendor.name}
                </div>
                {vendor.docs && (
                    <a href={vendor.docs} target="_blank" rel="noopener noreferrer" className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 mt-1">
                        API docs <ExternalLink className="w-3 h-3" />
                    </a>
                )}
                <label className="inline-flex items-center gap-1.5 text-xs mt-2 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        data-testid={`vendor-enable-${vendor.key}`}
                    />
                    <span className={enabled ? "text-emerald-700 font-medium" : "text-muted-foreground"}>{enabled ? "Enabled" : "Disabled"}</span>
                </label>
            </div>
            <div className="space-y-2">
                {vendor.fields.map((f) => {
                    const isSecret = f.includes("secret") || f.includes("password") || f.includes("token") || f.includes("private_key");
                    const savedSecret = isSecret && (state._secrets_set || []).includes(f);
                    return (
                        <div key={f}>
                            <Label className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{f.replace(/_/g, " ")}</Label>
                            <Input
                                type={isSecret ? "password" : "text"}
                                value={form[f] || ""}
                                onChange={(e) => setForm({ ...form, [f]: e.target.value })}
                                placeholder={(savedSecret || (state[f] && !isSecret)) ? "•••••• (saved)" : `Enter ${f}`}
                                className="rounded-none h-8 text-sm"
                                data-testid={`vendor-field-${vendor.key}-${f}`}
                            />
                        </div>
                    );
                })}
            </div>
            <div className="flex flex-col items-end gap-1">
                <Button
                    onClick={() => onSave({ ...form, enabled })}
                    disabled={saving || (!hasKeys && enabled)}
                    className="rounded-none h-8 text-xs"
                    data-testid={`vendor-save-${vendor.key}`}
                >
                    <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : "Save"}
                </Button>
                {state.updated_at && (
                    <span className="text-[10px] text-muted-foreground">Updated {new Date(state.updated_at).toLocaleDateString()}</span>
                )}
            </div>
        </div>
    );
}
